import { LinkOutlined } from "@ant-design/icons"
import { type ItemType, type MenuItemType } from "antd/es/menu/interface"
import { type ColumnType } from "antd/es/table/interface"
import React from "react"

import Icon from "../../../Icon"
import { RowActionsMenu } from "../../../components/EntityTable/RowActionsMenu.tsx"
import type { ListRow } from "../redux/global-map.types.ts"

export type GlobalMapListRowAction = "view" | "edit"

// A plain `(key) => string` rather than the real `i18next` `TFunction` —
// `TFunction`'s return type isn't structurally assignable to `ReactNode`/
// `ColumnTitle` without a cast at every call site; `getLeadsColumnActions`'
// own `Translate` type makes the same simplification for the same reason.
type Translate = (key: string, options?: Record<string, unknown>) => string

interface GetGlobalMapListColumnActionsProps {
  t: Translate
  // Only Project has a real, existing Edit destination distinct from View
  // (`RoutesPaths.project.edit`, the same route `getProjectsColumnActions`'s
  // own "Edit Details" item uses) — a Lead has none: `getLeadsColumnActions`
  // (the real Leads list's own row menu) offers only "view_details"/
  // "archive", because Lead editing happens in place on the same workspace
  // View already opens (see docs/kb/decisions/2026-08-15-workspace-cards-
  // edit-in-place.md). So this column offers no Lead "Edit" item at all
  // rather than duplicating View or inventing a route that doesn't exist —
  // `editAllowed` only ever gates the Project branch.
  editAllowed: boolean
  onSelectRow: (row: ListRow, action: GlobalMapListRowAction) => void
}

/**
 * Mirrors `getProjectsColumnActions`'s factory shape — a column config
 * built from callbacks/flags the page computes once, rendering through the
 * shared `RowActionsMenu` rather than a bespoke menu.
 */
export const getGlobalMapListColumnActions = ({
  t,
  editAllowed,
  onSelectRow
}: GetGlobalMapListColumnActionsProps): ColumnType<ListRow> => {
  const getItems = (row: ListRow): ItemType<MenuItemType>[] => {
    // View details is offered for every entity, ungated — it mirrors the
    // row's own click-to-select/navigate behavior, the same convention
    // `getProjectsColumnActions`' own View item follows.
    const items: ItemType<MenuItemType>[] = [
      {
        key: "view",
        label: t("globalMap.list.actions.viewDetails"),
        icon: <LinkOutlined />,
        onClick: () => onSelectRow(row, "view")
      }
    ]

    if (row.entity_type === "project" && editAllowed) {
      items.push({
        key: "edit",
        label: t("globalMap.list.actions.edit"),
        icon: <Icon type="Edit" />,
        onClick: () => onSelectRow(row, "edit")
      })
    }

    return items
  }

  return {
    title: t("globalMap.list.columnActions"),
    key: "actions",
    fixed: "right",
    width: 72,
    align: "right",
    render: (_, row) => {
      const items = getItems(row)
      if (!items.length) return null

      return (
        <RowActionsMenu
          items={items}
          buttonProps={{ "aria-label": t("tableActions.rowMenu") }}
        />
      )
    }
  }
}
